/**
 * The accent engine: one colour the player chose, turned into the accent
 * tokens the shell paints with, readable on whichever base (dark or light)
 * the game shows. Exported as `@artibens/app-shell/accent` (and from the
 * package root).
 *
 * Pure maths, no DOM, no clock, no randomness: the same input gives the same
 * hex on a phone, in Node and in a test. `applyAccent` (`accentApply.ts`) is
 * the one function that touches a page, and it only sets the custom
 * properties this module computed.
 *
 * ## What it does
 *
 * `resolveAccent(hex, base)` keeps the colour's HUE, keeps as much of its
 * CHROMA as the sRGB gamut allows at the lightness it ends at, and moves its
 * LIGHTNESS (in OKLCH) only as far as it must for every promise of the shell's
 * token contract (`tokens.css`, INTEGRATION.md section 8) to hold:
 *
 *   --ui-accent       text and glyphs on all five ground steps   >= 4.5:1
 *                     (so also the focus ring and a selected
 *                     outline, which need 3:1)
 *   --ui-on-accent    text on the accent and on the deep accent  >= 4.5:1,
 *                     near-black or near-white, whichever reads better
 *   --ui-accent-deep  a step deeper than the accent; on-accent still reads
 *   --ui-celebrate    the words of a celebration on a surface    >= 4.5:1
 *   --ui-deep-from, --ui-deep-to   the results gradient: on-deep text on
 *                     both stops, on its facts panel and on a marked fact
 *   --ui-deep-ink     the words on a light pill on that gradient >= 4.5:1
 *
 * A chosen card (10% accent over the card), the focus ring and a selected
 * swatch's ring are mixed from `--ui-accent` where they are drawn, so they
 * need no token of their own: they follow from the accent's guarantees.
 *
 * Lightness also stays inside a band per base (`ACCENT_LIGHTNESS`): on dark
 * never so pale that the accent reads as the ink, on light never so deep that
 * it reads as black. Inside the band, a colour that already passes is left
 * exactly as it is.
 *
 * ## Presets
 *
 * `ACCENT_PRESETS` are named colours, each with a value tuned by hand for
 * each base (`dark`, `light`); the preset's `hex` (its dark value) is what a
 * game stores. A preset resolves to its tuned value and is never "adjusted".
 *
 * ## The colour maths
 *
 * Written here, not imported (a colour library would be a runtime dependency
 * for a hundred lines): sRGB <-> linear light (IEC 61966-2-1), linear sRGB
 * <-> OKLab (Björn Ottosson's matrices, 2020), OKLab <-> OKLCH, a gamut clamp
 * that lowers chroma at fixed lightness and hue, WCAG 2.x relative luminance
 * and contrast, and a colour-vision-deficiency simulation (Machado, Oliveira &
 * Fernandes 2009, severity 1) for `cvdDistance`.
 */

export type AccentBase = 'dark' | 'light';

/** A gamma-encoded sRGB colour, each channel 0..1. */
export type Rgb = readonly [number, number, number];

/** OKLab: lightness 0..1 and the two opponent axes. */
export type Oklab = readonly [number, number, number];

/** OKLCH: lightness 0..1, chroma (0 to about 0.37 inside sRGB), hue in degrees. */
export interface Oklch {
  readonly l: number;
  readonly c: number;
  readonly h: number;
}

/** WCAG 2.x AA for text (1.4.3). */
export const TEXT_CONTRAST = 4.5;
/** WCAG 2.x AA for a line or shape that carries meaning (1.4.11). */
export const GRAPHIC_CONTRAST = 3;

/* ------------------------------------------------------------- hex and sRGB */

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** `#rgb`, `#rrggbb` (with or without `#`, any case) as `#rrggbb` lower case, or `null`. */
export function normaliseHex(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const match = HEX_PATTERN.exec(value.trim());
  const digits = match?.[1];
  if (digits === undefined) return null;
  const full =
    digits.length === 3
      ? digits
          .split('')
          .map((digit) => digit + digit)
          .join('')
      : digits;
  return `#${full.toLowerCase()}`;
}

/** A hex colour as sRGB 0..1, or `null` when it is not one. */
export function hexToRgb(hex: string): Rgb | null {
  const normal = normaliseHex(hex);
  if (normal === null) return null;
  return [
    Number.parseInt(normal.slice(1, 3), 16) / 255,
    Number.parseInt(normal.slice(3, 5), 16) / 255,
    Number.parseInt(normal.slice(5, 7), 16) / 255,
  ];
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** sRGB 0..1 (clamped) as `#rrggbb`. */
export function rgbToHex(rgb: Rgb): string {
  return `#${rgb
    .map((channel) =>
      Math.round(clamp01(channel) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/** One gamma-encoded sRGB channel to linear light. */
export function srgbToLinear(channel: number): number {
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

/** One linear-light channel to gamma-encoded sRGB. */
export function linearToSrgb(channel: number): number {
  const sign = channel < 0 ? -1 : 1;
  const magnitude = Math.abs(channel);
  return magnitude <= 0.0031308 ? channel * 12.92 : sign * (1.055 * magnitude ** (1 / 2.4) - 0.055);
}

/* -------------------------------------------------------------------- OKLab */

/** Linear sRGB to OKLab. */
export function linearToOklab([r, g, b]: Rgb): Oklab {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

/** OKLab to linear sRGB. Unclamped: a channel outside 0..1 means out of gamut. */
export function oklabToLinear([lightness, a, b]: Oklab): Rgb {
  const l = (lightness + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (lightness - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (lightness - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

/** OKLCH to OKLab. */
export function oklchToOklab({ l, c, h }: Oklch): Oklab {
  const radians = (h * Math.PI) / 180;
  return [l, c * Math.cos(radians), c * Math.sin(radians)];
}

/** OKLab to OKLCH; an achromatic colour's hue is 0. */
export function oklabToOklch([l, a, b]: Oklab): Oklch {
  const c = Math.hypot(a, b);
  const h = c < 1e-6 ? 0 : ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
  return { l, c, h };
}

/** A hex colour in OKLab. */
export function hexToOklab(hex: string): Oklab | null {
  const rgb = hexToRgb(hex);
  if (rgb === null) return null;
  return linearToOklab([srgbToLinear(rgb[0]), srgbToLinear(rgb[1]), srgbToLinear(rgb[2])]);
}

/** A hex colour in OKLCH. */
export function hexToOklch(hex: string): Oklch | null {
  const lab = hexToOklab(hex);
  return lab === null ? null : oklabToOklch(lab);
}

const GAMUT_EPSILON = 1e-6;

function inGamut(linear: Rgb): boolean {
  return linear.every((channel) => channel >= -GAMUT_EPSILON && channel <= 1 + GAMUT_EPSILON);
}

/**
 * The most chroma sRGB can show at this lightness and hue (a binary search,
 * to about 1e-5).
 */
export function maxChroma(l: number, h: number): number {
  if (l <= 0 || l >= 1) return 0;
  let low = 0;
  let high = 0.4;
  for (let step = 0; step < 24; step += 1) {
    const middle = (low + high) / 2;
    if (inGamut(oklabToLinear(oklchToOklab({ l, c: middle, h })))) low = middle;
    else high = middle;
  }
  return low;
}

/**
 * An OKLCH colour as `#rrggbb`, brought into the sRGB gamut by lowering its
 * chroma at the same lightness and hue (never by clipping channels, which
 * would shift the hue).
 */
export function oklchToHex(colour: Oklch): string {
  const l = clamp01(colour.l);
  let lab = oklchToOklab({ l, c: Math.max(0, colour.c), h: colour.h });
  if (!inGamut(oklabToLinear(lab))) {
    lab = oklchToOklab({ l, c: maxChroma(l, colour.h), h: colour.h });
  }
  const linear = oklabToLinear(lab);
  return rgbToHex([linearToSrgb(linear[0]), linearToSrgb(linear[1]), linearToSrgb(linear[2])]);
}

/** The straight-line distance between two colours in OKLab (0 = the same colour). */
export function oklabDistance(a: string, b: string): number {
  const p = hexToOklab(a);
  const q = hexToOklab(b);
  if (p === null || q === null) return Number.POSITIVE_INFINITY;
  return Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]);
}

/* ----------------------------------------------------------------- contrast */

/** WCAG 2.x relative luminance of a hex colour, 0 (black) to 1 (white). */
export function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex) ?? [0, 0, 0];
  const [r, g, b] = rgb.map(srgbToLinear) as unknown as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.x contrast ratio of two hex colours, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * `share` (0..1) of `top` over `under`, mixed per sRGB channel: what
 * `color-mix(in srgb, top N%, under)` paints, and what a translucent layer
 * of `top` over an opaque `under` shows.
 */
export function mixHex(top: string, share: number, under: string): string {
  const a = hexToRgb(top) ?? [0, 0, 0];
  const b = hexToRgb(under) ?? [0, 0, 0];
  return rgbToHex([
    a[0] * share + b[0] * (1 - share),
    a[1] * share + b[1] * (1 - share),
    a[2] * share + b[2] * (1 - share),
  ]);
}

/* ---------------------------------------------------- colour-vision deficiency */

type Matrix = readonly [Rgb, Rgb, Rgb];

/** Machado, Oliveira & Fernandes (2009), severity 1, on linear sRGB. */
const CVD_MATRICES: Readonly<Record<'protanopia' | 'deuteranopia' | 'tritanopia', Matrix>> = {
  protanopia: [
    [0.152286, 1.052583, -0.204868],
    [0.114503, 0.786281, 0.099216],
    [-0.003882, -0.048116, 1.051998],
  ],
  deuteranopia: [
    [0.367322, 0.860646, -0.227968],
    [0.280085, 0.672501, 0.047413],
    [-0.01182, 0.04294, 0.968881],
  ],
  tritanopia: [
    [1.255528, -0.076749, -0.178779],
    [-0.078411, 0.930809, 0.147602],
    [0.004733, 0.691367, 0.3039],
  ],
};

export type VisionKind = 'normal' | keyof typeof CVD_MATRICES;
export const VISION_KINDS: readonly VisionKind[] = [
  'normal',
  'protanopia',
  'deuteranopia',
  'tritanopia',
];

/** How a colour looks under `kind`, in OKLab. */
export function simulateVision(hex: string, kind: VisionKind): Oklab {
  const rgb = hexToRgb(hex) ?? [0, 0, 0];
  const linear: Rgb = [srgbToLinear(rgb[0]), srgbToLinear(rgb[1]), srgbToLinear(rgb[2])];
  if (kind === 'normal') return linearToOklab(linear);
  const matrix = CVD_MATRICES[kind];
  const row = (k: 0 | 1 | 2): number =>
    clamp01(matrix[k][0] * linear[0] + matrix[k][1] * linear[1] + matrix[k][2] * linear[2]);
  return linearToOklab([row(0), row(1), row(2)]);
}

/**
 * How far apart two colours stay for everybody: the OKLab distance (x 100)
 * under normal vision, protanopia, deuteranopia and tritanopia, whichever is
 * smallest. Two board states (a selected cell, a hinted one) that sit side by
 * side should keep this well above 5.
 */
export function cvdDistance(a: string, b: string): number {
  let worst = Number.POSITIVE_INFINITY;
  for (const kind of VISION_KINDS) {
    const p = simulateVision(a, kind);
    const q = simulateVision(b, kind);
    worst = Math.min(worst, 100 * Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]));
  }
  return worst;
}

/* ------------------------------------------------------------------ fitting */

/** The step the lightness search moves by, in OKLCH lightness. */
const LIGHTNESS_STEP = 0.004;

/**
 * Move `seed`'s lightness in `direction` (+1 lighter, -1 darker) until
 * `passes` holds, keeping its hue and as much of its chroma as the gamut
 * allows at each lightness. The seed itself is tried first. Returns the first
 * hex that passes, or the last one tried (white or black) when none does.
 */
export function fitLightness(
  seed: Oklch,
  passes: (hex: string) => boolean,
  direction: 1 | -1,
): string {
  let l = clamp01(seed.l);
  let hex = oklchToHex({ l, c: seed.c, h: seed.h });
  for (;;) {
    if (passes(hex)) return hex;
    if (direction > 0 ? l >= 1 : l <= 0) return hex;
    l = clamp01(l + direction * LIGHTNESS_STEP);
    hex = oklchToHex({ l, c: seed.c, h: seed.h });
  }
}

/* ------------------------------------------------------------------ grounds */

/**
 * The five steps of a base's ground ladder (`tokens.css`): everything the
 * accent is drawn on as text. A game with neutrals of its own passes them.
 */
export interface AccentGrounds {
  readonly sunken: string;
  readonly ground: string;
  readonly card: string;
  readonly surface: string;
  readonly surface2: string;
}

/** The house theme's ladders (`theme.css`); `test/accent.test.ts` holds them to it. */
export const HOUSE_GROUNDS: Readonly<Record<AccentBase, AccentGrounds>> = {
  dark: {
    sunken: '#000000',
    ground: '#000000',
    card: '#121212',
    surface: '#1c1c1c',
    surface2: '#2a2a2a',
  },
  light: {
    sunken: '#f2f2f2',
    ground: '#ffffff',
    card: '#ffffff',
    surface: '#ffffff',
    surface2: '#ececec',
  },
};

/**
 * Where the accent's lightness may sit, per base (OKLCH L). On dark, never
 * paler than `max` (above it the accent reads as the ink); on light, never
 * deeper than `min` (below it, as black). Contrast moves it further when it
 * must; inside the band a colour that passes is kept as it is.
 */
export const ACCENT_LIGHTNESS: Readonly<
  Record<AccentBase, { readonly min: number; readonly max: number }>
> = {
  dark: { min: 0, max: 0.85 },
  light: { min: 0.32, max: 1 },
};

/** How much deeper `--ui-accent-deep` is than the accent (OKLCH L). */
export const ACCENT_DEEP_STEP = 0.06;

/**
 * The results gradient's two stops start here (OKLCH L) and go deeper until
 * the on-deep words read on each, on the facts panel and on a marked fact.
 */
export const DEEP_LIGHTNESS = { from: 0.52, to: 0.38 } as const;

/**
 * A picked colour counts as "adjusted" when the accent shown is at least
 * this far from it in OKLab (about one just-noticeable difference and a half).
 */
export const ADJUSTED_DISTANCE = 0.03;

/** The pale ink on a deep accent fill. */
export const LIGHT_INK = '#ffffff';

/** The dark ink on a pale accent fill: a near-black of the accent's own hue. */
export function darkInkFor(h: number, c: number): string {
  return oklchToHex({ l: 0.27, c: Math.min(c * 0.45, 0.05), h });
}

/* ------------------------------------------------------------------ presets */

export interface AccentPreset {
  /** A stable id (a game keys its words on it). */
  readonly id: string;
  /** The English name; a game passes its own words through `AccentPicker`. */
  readonly name: string;
  /** What a game stores for this preset: the preset's dark value. */
  readonly hex: string;
  /** The value on a dark base. */
  readonly dark: string;
  /** The value on a light base. */
  readonly light: string;
}

/**
 * Nine named accents, spaced round the hue circle, plus a quiet one. The
 * first three are TwinTen's former themes, value for value, so a player who
 * had one keeps it. Each is tuned per base and passes every promise on the
 * house grounds (`test/accent.test.ts`).
 */
export const ACCENT_PRESETS: readonly AccentPreset[] = [
  { id: 'lagoon', name: 'Lagoon', hex: '#3ecfba', dark: '#3ecfba', light: '#0b7369' },
  { id: 'ember', name: 'Ember', hex: '#e29a4a', dark: '#e29a4a', light: '#a3541c' },
  { id: 'mulberry', name: 'Mulberry', hex: '#dd6fa6', dark: '#de70a7', light: '#8d2f5f' },
  { id: 'harbour', name: 'Harbour', hex: '#6fa8ff', dark: '#6fa8ff', light: '#2459c4' },
  { id: 'iris', name: 'Iris', hex: '#a48bff', dark: '#a48bff', light: '#6a46d1' },
  { id: 'poppy', name: 'Poppy', hex: '#ff7f7a', dark: '#ff7f7a', light: '#b8322f' },
  { id: 'saffron', name: 'Saffron', hex: '#e6c14a', dark: '#e6c14a', light: '#7d6300' },
  { id: 'fern', name: 'Fern', hex: '#62d17f', dark: '#62d17f', light: '#1f7a3c' },
  { id: 'graphite', name: 'Graphite', hex: '#b4bcc6', dark: '#b4bcc6', light: '#535c68' },
];

/** The accent a game starts on, and what anything unreadable falls back to: Lagoon. */
export const DEFAULT_ACCENT = '#3ecfba';

/** The preset a stored value names (by its `hex`, or by its value on either base), or `null`. */
export function findPreset(
  value: string,
  presets: readonly AccentPreset[] = ACCENT_PRESETS,
): AccentPreset | null {
  const hex = normaliseHex(value);
  if (hex === null) return null;
  return (
    presets.find(
      (preset) =>
        normaliseHex(preset.hex) === hex ||
        normaliseHex(preset.dark) === hex ||
        normaliseHex(preset.light) === hex,
    ) ?? null
  );
}

/* ---------------------------------------------------------------- signature */

/**
 * A game's signature colour (since 1.4.0): the accent that is the game's own,
 * the one a new player starts on, named after the game. `AccentPicker` draws
 * it as the first swatch, before the shared presets.
 *
 * One colour, not a pair: the engine fits it to each base as it fits any
 * colour (`resolveAccent`), so it reads on black and on white alike. It
 * resolves as a preset (`signaturePreset`), so it is never reported as
 * "adjusted". The house's three are in INTEGRATION.md section 4.6; each game
 * keeps its own as one named constant.
 */
export interface AccentSignature {
  /** The game's name, in its own spelling: the swatch's name ("Sample"). */
  readonly name: string;
  /** The colour: what the game stores, and what both bases start from. */
  readonly hex: string;
  /** A stable id for the swatch (its test id's suffix). Default `'signature'`. */
  readonly id?: string | undefined;
}

/** The id a signature's swatch takes when it names none. */
export const SIGNATURE_ID = 'signature';

/**
 * A signature as a preset: its `hex` is both bases' seed (the engine fits it
 * to each). An unreadable `hex` falls back to `DEFAULT_ACCENT`.
 */
export function signaturePreset(signature: AccentSignature): AccentPreset {
  const hex = normaliseHex(signature.hex) ?? DEFAULT_ACCENT;
  return {
    id: signature.id ?? SIGNATURE_ID,
    name: signature.name,
    hex,
    dark: hex,
    light: hex,
  };
}

/**
 * How far apart (OKLab) two swatches must be on both house bases to be
 * offered side by side: the spacing the shared presets keep among themselves.
 */
export const SWATCH_SPACING = 0.07;

/**
 * The swatch row with a game's signature first: the signature, then the
 * shared presets in their order. A shared preset that would look the same as
 * the signature on either house base (closer than `SWATCH_SPACING`, the same
 * colour, or the same id) gives way, so no colour is offered twice. A player
 * who had chosen that preset keeps the colour: it reads as a custom one.
 *
 * Pass the result as `presets` to `resolveAccent` too, so the signature
 * resolves as a preset (never "adjusted") wherever the game resolves it.
 */
export function presetsWithSignature(
  signature: AccentSignature | undefined,
  presets: readonly AccentPreset[] = ACCENT_PRESETS,
): readonly AccentPreset[] {
  if (signature === undefined) return presets;
  // A signature that IS a shared preset's colour takes that preset's tuned
  // values, so it looks exactly as the preset did.
  const own = signaturePreset(signature);
  const twin = findPreset(own.hex, presets);
  const first: AccentPreset = twin === null ? own : { ...own, dark: twin.dark, light: twin.light };
  const shown = {
    dark: resolveAccent(first.hex, 'dark', { presets: [first] }).accent,
    light: resolveAccent(first.hex, 'light', { presets: [first] }).accent,
  };
  const apart = (preset: AccentPreset): boolean =>
    preset.id !== first.id &&
    findPreset(first.hex, [preset]) === null &&
    oklabDistance(preset.dark, shown.dark) > SWATCH_SPACING &&
    oklabDistance(preset.light, shown.light) > SWATCH_SPACING;
  return [first, ...presets.filter(apart)];
}

/**
 * What a stored accent is looked up in when a game has a signature: the row
 * (`presetsWithSignature`), then the shared presets that gave way to the
 * signature. A player who chose one of those keeps its tuned colours to the
 * bit, and `AccentPicker` keeps its swatch in the row, chosen, while it is
 * the value. Pass it as `presets` to `resolveAccent` (since 1.4.0).
 */
export function presetsToResolve(
  signature: AccentSignature | undefined,
  presets: readonly AccentPreset[] = ACCENT_PRESETS,
): readonly AccentPreset[] {
  const row = presetsWithSignature(signature, presets);
  return [...row, ...presets.filter((preset) => !row.includes(preset))];
}

/* ------------------------------------------------------------------ resolve */

/** The `--ui-*` tokens the engine sets: the shell's identity tokens. */
export const ACCENT_TOKEN_NAMES = [
  '--ui-accent',
  '--ui-accent-deep',
  '--ui-on-accent',
  '--ui-celebrate',
  '--ui-deep-from',
  '--ui-deep-to',
  '--ui-deep-ink',
] as const;

export type AccentTokenName = (typeof ACCENT_TOKEN_NAMES)[number];
export type AccentTokens = Readonly<Record<AccentTokenName, string>>;

export interface AccentOptions {
  /** The ladder the accent is drawn on. Default: the house theme's for the base. */
  readonly grounds?: AccentGrounds | undefined;
  /** The words on the results gradient (`--ui-on-deep`). Default white, as the house's. */
  readonly onDeep?: string | undefined;
  /** The presets a stored value is looked up in. Default `ACCENT_PRESETS`. */
  readonly presets?: readonly AccentPreset[] | undefined;
}

export interface AccentResult {
  /** What was asked for, normalised (`DEFAULT_ACCENT` when it was not a colour). */
  readonly requested: string;
  /** The preset it names, if any. */
  readonly preset: AccentPreset | null;
  /** The colour the engine started from: the preset's value for the base, or the request. */
  readonly seed: string;
  readonly accent: string;
  readonly accentDeep: string;
  readonly onAccent: string;
  readonly celebrate: string;
  readonly deepFrom: string;
  readonly deepTo: string;
  readonly deepInk: string;
  /** Whether the accent shown differs visibly from a custom colour asked for. Never for a preset. */
  readonly adjusted: boolean;
  /**
   * Whether a lightness inside the band (`ACCENT_LIGHTNESS`) keeps every
   * promise on these grounds. True on the house's ladder and any ladder near
   * it; false only for grounds the band cannot serve (a mid-grey page), and
   * then `accent` is the best the search found, not a guarantee.
   */
  readonly fits: boolean;
  /** The same values by token name, for `applyAccent`. */
  readonly tokens: AccentTokens;
}

function grounds(of: AccentGrounds): readonly string[] {
  return [of.sunken, of.ground, of.card, of.surface, of.surface2];
}

/** The ink (dark or light) that reads best on `fill`. */
function inkOn(fill: string, darkInk: string): string {
  return contrastRatio(darkInk, fill) >= contrastRatio(LIGHT_INK, fill) ? darkInk : LIGHT_INK;
}

/**
 * Whether `accent` keeps every promise of the accent on these grounds: text
 * on all five steps, and an ink (near-black or white) that reads on it.
 */
export function accentPasses(accent: string, base: AccentBase, ladder: AccentGrounds): boolean {
  const lch = hexToOklch(accent);
  if (lch === null) return false;
  const band = ACCENT_LIGHTNESS[base];
  if (lch.l > band.max + 0.002 || lch.l < band.min - 0.002) return false;
  if (!grounds(ladder).every((ground) => contrastRatio(accent, ground) >= TEXT_CONTRAST)) {
    return false;
  }
  return contrastRatio(inkOn(accent, darkInkFor(lch.h, lch.c)), accent) >= TEXT_CONTRAST;
}

/**
 * The accent tokens for `value` on `base`. Anything that is not a colour
 * resolves as `DEFAULT_ACCENT`.
 */
export function resolveAccent(
  value: string,
  base: AccentBase,
  options: AccentOptions = {},
): AccentResult {
  const ladder = options.grounds ?? HOUSE_GROUNDS[base];
  const onDeep = options.onDeep ?? LIGHT_INK;
  const presets = options.presets ?? ACCENT_PRESETS;
  const requested = normaliseHex(value) ?? DEFAULT_ACCENT;
  const preset = findPreset(requested, presets);
  const seed = normaliseHex(preset === null ? requested : preset[base]) ?? DEFAULT_ACCENT;
  const seedLch = hexToOklch(seed) ?? { l: 0.7, c: 0, h: 0 };
  const band = ACCENT_LIGHTNESS[base];

  // The accent: start inside the band, then go the readable way (lighter on
  // dark, deeper on light) until every promise holds.
  const start: Oklch = { ...seedLch, l: Math.min(band.max, Math.max(band.min, seedLch.l)) };
  const toward: 1 | -1 = base === 'dark' ? 1 : -1;
  let accent = fitLightness(start, (hex) => accentPasses(hex, base, ladder), toward);
  if (!accentPasses(accent, base, ladder)) {
    // The band's edge itself did not pass (a ladder far from the house's):
    // search the other way from the seed instead.
    accent = fitLightness(start, (hex) => accentPasses(hex, base, ladder), -toward as 1 | -1);
  }
  // A seed that already passes is kept to the bit, not re-encoded.
  if (accentPasses(seed, base, ladder)) accent = seed;
  const lch = hexToOklch(accent) ?? seedLch;
  const darkInk = darkInkFor(lch.h, lch.c);
  const onAccent = inkOn(accent, darkInk);

  // The deep accent: a step deeper, but never so deep the ink stops reading.
  const deepSeed: Oklch = { ...lch, l: lch.l - ACCENT_DEEP_STEP };
  const accentDeep = fitLightness(
    deepSeed,
    (hex) => contrastRatio(onAccent, hex) >= TEXT_CONTRAST,
    onAccent === LIGHT_INK ? -1 : 1,
  );

  // The celebration's words: on dark a paler sister of the accent, on light
  // the accent itself; either way readable on a surface.
  const celebrate =
    base === 'dark'
      ? fitLightness(
          { ...lch, l: Math.min(0.9, lch.l + 0.08), c: lch.c * 0.8 },
          (hex) => contrastRatio(hex, ladder.surface) >= TEXT_CONTRAST,
          1,
        )
      : accent;

  // The results gradient: two deep stops of the hue. The facts panel (16%
  // on-deep) can sit anywhere on the gradient, so BOTH stops are fitted so
  // the on-deep words read on the panel over them, and the darker one also
  // carries a marked fact (14% more over the panel).
  const reads = (hex: string): boolean => contrastRatio(onDeep, hex) >= TEXT_CONTRAST;
  const deepFrom = fitLightness(
    { ...lch, l: DEEP_LIGHTNESS.from },
    (hex) => reads(hex) && reads(mixHex(onDeep, 0.16, hex)),
    -1,
  );
  const deepTo = fitLightness(
    { ...lch, l: DEEP_LIGHTNESS.to },
    (hex) => {
      const panel = mixHex(onDeep, 0.16, hex);
      return reads(hex) && reads(panel) && reads(mixHex(onDeep, 0.14, panel));
    },
    -1,
  );

  const adjusted = preset === null && oklabDistance(requested, accent) >= ADJUSTED_DISTANCE;
  return {
    requested,
    preset,
    seed,
    accent,
    accentDeep,
    onAccent,
    celebrate,
    deepFrom,
    deepTo,
    deepInk: deepTo,
    adjusted,
    fits: accentPasses(accent, base, ladder),
    tokens: {
      '--ui-accent': accent,
      '--ui-accent-deep': accentDeep,
      '--ui-on-accent': onAccent,
      '--ui-celebrate': celebrate,
      '--ui-deep-from': deepFrom,
      '--ui-deep-to': deepTo,
      '--ui-deep-ink': deepTo,
    },
  };
}

/* ------------------------------------------------------------------- picker */

/**
 * The lightness the custom picker draws its colours at, per base (OKLCH L):
 * where most hues already read on that base, so what the sliders show is
 * close to what the player gets.
 */
export const PICKER_LIGHTNESS: Readonly<Record<AccentBase, number>> = {
  dark: 0.78,
  light: 0.5,
};

/** The colour the picker's two sliders name: a hue (0..360) and a vividness (0..1). */
export function pickerColour(hue: number, vividness: number, base: AccentBase): string {
  const l = PICKER_LIGHTNESS[base];
  const h = ((hue % 360) + 360) % 360;
  return oklchToHex({ l, c: clamp01(vividness) * maxChroma(l, h), h });
}

/** Where a colour sits on the picker's two sliders. */
export function pickerPosition(
  hex: string,
  base: AccentBase,
): { readonly hue: number; readonly vividness: number } {
  const lch = hexToOklch(hex) ?? { l: 0, c: 0, h: 0 };
  const room = maxChroma(PICKER_LIGHTNESS[base], lch.h);
  return {
    hue: Math.round(lch.h),
    vividness: room <= 0 ? 0 : Math.round(clamp01(lch.c / room) * 100) / 100,
  };
}

/** The hue families `hueFamily` names, round the OKLCH hue circle. */
export const HUE_FAMILIES = [
  'pink',
  'red',
  'orange',
  'yellow',
  'green',
  'teal',
  'blue',
  'purple',
] as const;

export type HueFamily = (typeof HUE_FAMILIES)[number];

/** Where each family starts, in OKLCH degrees; below 15 is pink, which wraps from 320. */
const HUE_STARTS: readonly (readonly [number, HueFamily])[] = [
  [15, 'red'],
  [40, 'orange'],
  [70, 'yellow'],
  [115, 'green'],
  [165, 'teal'],
  [215, 'blue'],
  [275, 'purple'],
  [320, 'pink'],
];

/**
 * The everyday name of an OKLCH hue, for a slider's spoken value ("210°,
 * blue"). For reference: sRGB red is at 29°, yellow 110°, green 142°, cyan
 * 195°, blue 264°, magenta 328°. A game words it (`AccentPicker`'s `hueName`).
 */
export function hueFamily(hue: number): HueFamily {
  const h = ((hue % 360) + 360) % 360;
  let family: HueFamily = 'pink';
  for (const [start, name] of HUE_STARTS) if (h >= start) family = name;
  return family;
}

/** The hue circle at the picker's lightness and full vividness, `stops + 1` colours from 0 to 360. */
export function hueStops(base: AccentBase, stops = 12): readonly string[] {
  const colours: string[] = [];
  for (let index = 0; index <= stops; index += 1) {
    colours.push(pickerColour((360 * index) / stops, 1, base));
  }
  return colours;
}

/** The hue slider's track: the hue circle at the picker's lightness, as a CSS gradient. */
export function hueTrack(base: AccentBase, stops = 12): string {
  return `linear-gradient(to right, ${hueStops(base, stops).join(', ')})`;
}

/** The custom swatch before a custom colour is chosen: the hue circle as a wheel. */
export function hueWheel(base: AccentBase, stops = 12): string {
  return `conic-gradient(${hueStops(base, stops).join(', ')})`;
}

/** The vividness slider's track: from grey to the hue at full vividness. */
export function vividnessTrack(hue: number, base: AccentBase): string {
  return `linear-gradient(to right, ${pickerColour(hue, 0, base)}, ${pickerColour(hue, 0.5, base)}, ${pickerColour(hue, 1, base)})`;
}
